import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import type {
  QgammonLeaderboardResponse,
  QgammonMatchSummary,
  QgammonMeResponse,
  QgammonMyMatchesResponse,
  QgammonReplay,
} from '@/types/qgammon';

/**
 * QGammon API client. Talks to qgammon-api (REST) — leaderboard + match
 * history for the `/backgammon` surface. Live play (Socket.IO to the
 * qgammon-gameserver) lands in Fase 6 part 2 with the match board.
 *
 * Auth: a resource-scoped Logto Bearer (audience `https://qgammon.qlash.gg/api`)
 * minted by getQgammonToken(), with a dev-bypass fallback (`X-Dev-Bypass` +
 * optional `X-Dev-User`) for local testing against the MacMini before a user is
 * signed in. The resolution order (Logto → dev-bypass → none) is centralised in
 * `@/lib/qgammon/auth` (qgammonAuthHeaders) and shared with every gameserver
 * touchpoint — the per-user Logto identity is the fix for the shared "Dev"
 * dev-bypass user that made two humans collide on one userId.
 *
 * Dev: env.qgammonApiUrl = '/qgammon-api', Vite proxies to the OVH PROD API
 * (`https://qgammon.qlash.gg`) by default — override VITE_QGAMMON_API_URL for
 * a local backend. Prod: 'https://qgammon.qlash.gg'.
 */
export const qgammonApi = createApi({
  reducerPath: 'qgammonApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qgammonApiUrl,
      prepareHeaders: async (headers) => {
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        // Logto Bearer if signed in, else dev-bypass headers (dev only).
        const auth = await qgammonAuthHeaders();
        for (const [k, v] of Object.entries(auth)) headers.set(k, v);
        return headers;
      },
    }),
    {
      retryCondition: (error, _args, { attempt }) => {
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  ),
  keepUnusedDataFor: 120,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Leaderboard', 'Me', 'MyMatches', 'Match'],
  endpoints: (builder) => ({
    /** ELO leaderboard (`GET /leaderboard`) → `{ players: [...] }`. */
    getLeaderboard: builder.query<QgammonLeaderboardResponse, void>({
      query: () => '/leaderboard',
      providesTags: ['Leaderboard'],
    }),
    /** Authenticated caller's identity (`GET /auth/me`). Requires auth/dev-bypass. */
    getMe: builder.query<QgammonMeResponse, void>({
      query: () => '/auth/me',
      providesTags: ['Me'],
      keepUnusedDataFor: 300,
    }),
    /** The caller's matches (`GET /matches/mine`). Requires auth/dev-bypass. */
    getMyMatches: builder.query<QgammonMyMatchesResponse, void>({
      query: () => '/matches/mine',
      providesTags: ['MyMatches'],
    }),
    /** Full replay (`GET /matches/:id/replay`). Finished matches only (404 else). */
    getReplay: builder.query<QgammonReplay, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}/replay`,
    }),
    /** A single match summary (`GET /matches/:id`) — for the replay's W/B
     * perspective (maps the viewer's id → colour). Finished matches only. */
    getMatch: builder.query<{ match: QgammonMatchSummary }, string>({
      query: (matchId) => `/matches/${encodeURIComponent(matchId)}`,
      providesTags: (_r, _e, id) => [{ type: 'Match' as const, id }],
    }),
  }),
});

export const {
  useGetLeaderboardQuery,
  useGetMeQuery,
  useGetMyMatchesQuery,
  useGetReplayQuery,
  useGetMatchQuery,
} = qgammonApi;
